"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SafeActionConfigSchema = exports.ActionCreateSchema = exports.ActionConfigSchema = exports.GitHubDispatchActionCreateSchema = exports.SafeGitHubDispatchActionConfigSchema = exports.GitHubDispatchActionConfigSchema = exports.SlackActionConfigSchema = exports.WebhookActionCreateSchema = exports.SafeWebhookActionConfigSchema = exports.WebhookActionConfigSchema = exports.RequestHeaderSchema = exports.AvailableWebhookApiSchema = exports.ActionTypeSchema = exports.ProjectNotificationEventTypeSchema = exports.TriggerEventSourceSchema = exports.EventActionSchema = exports.TriggerEventSource = void 0;
exports.isWebhookActionConfig = isWebhookActionConfig;
exports.isSlackActionConfig = isSlackActionConfig;
exports.isWebhookAction = isWebhookAction;
exports.isSafeWebhookActionConfig = isSafeWebhookActionConfig;
exports.convertToSafeWebhookConfig = convertToSafeWebhookConfig;
exports.isGitHubDispatchActionConfig = isGitHubDispatchActionConfig;
exports.isGitHubDispatchAction = isGitHubDispatchAction;
exports.isSafeGitHubDispatchActionConfig = isSafeGitHubDispatchActionConfig;
exports.convertToSafeGitHubDispatchConfig = convertToSafeGitHubDispatchConfig;
const zod_1 = require("zod");
var TriggerEventSource;
(function (TriggerEventSource) {
    TriggerEventSource["Prompt"] = "prompt";
    TriggerEventSource["Monitor"] = "monitor";
    // ProjectNotification groups platform "something went wrong" events (e.g.
    // failed exports, blocked evaluators). The specific event lives in the
    // trigger's eventActions.
    TriggerEventSource["ProjectNotification"] = "project-notification";
})(TriggerEventSource || (exports.TriggerEventSource = TriggerEventSource = {}));
exports.EventActionSchema = zod_1.z.enum(["created", "updated", "deleted"]);
exports.TriggerEventSourceSchema = zod_1.z.enum([
    TriggerEventSource.Prompt,
    TriggerEventSource.Monitor,
    TriggerEventSource.ProjectNotification,
]);
/**
 * ProjectNotificationEventTypeSchema enumerates the platform "something went
 * wrong" events routable to project notification channels. Stored as a
 * `project-notification`-source trigger's eventActions. Client-safe so
 * settings UI can list them. Add new event types here.
 */
exports.ProjectNotificationEventTypeSchema = zod_1.z.enum([
    "blob-export-failed",
    "evaluator-blocked",
]);
exports.ActionTypeSchema = zod_1.z.enum(["WEBHOOK", "SLACK", "GITHUB_DISPATCH"]);
exports.AvailableWebhookApiSchema = zod_1.z.partialRecord(zod_1.z.enum(["prompt", "monitor", "project-notification"]), zod_1.z.enum(["v1"]));
exports.RequestHeaderSchema = zod_1.z.object({
    secret: zod_1.z.boolean(),
    value: zod_1.z.string(),
});
exports.WebhookActionConfigSchema = zod_1.z.object({
    type: zod_1.z.literal("WEBHOOK"),
    url: zod_1.z.url(), // Basic URL validation only - manual security validation called separately
    headers: zod_1.z.record(zod_1.z.string(), zod_1.z.string()).optional(), // deprecated field, use requestHeaders instead
    requestHeaders: zod_1.z.record(zod_1.z.string(), exports.RequestHeaderSchema).optional(), // might not exist on legacy webhooks
    displayHeaders: zod_1.z.record(zod_1.z.string(), exports.RequestHeaderSchema).optional(), // might not exist on legacy webhooks
    apiVersion: exports.AvailableWebhookApiSchema,
    secretKey: zod_1.z.string(),
    displaySecretKey: zod_1.z.string(),
    lastFailingExecutionId: zod_1.z.string().nullish(),
});
exports.SafeWebhookActionConfigSchema = exports.WebhookActionConfigSchema.omit({
    secretKey: true,
    headers: true,
    requestHeaders: true,
});
exports.WebhookActionCreateSchema = exports.WebhookActionConfigSchema.omit({
    secretKey: true,
    displaySecretKey: true,
    headers: true, // don't use legacy field anymore
    displayHeaders: true,
});
exports.SlackActionConfigSchema = zod_1.z.object({
    type: zod_1.z.literal("SLACK"),
    channelId: zod_1.z.string(),
    channelName: zod_1.z.string(),
    messageTemplate: zod_1.z.string().optional(),
});
exports.GitHubDispatchActionConfigSchema = zod_1.z.object({
    type: zod_1.z.literal("GITHUB_DISPATCH"),
    url: zod_1.z.url(),
    eventType: zod_1.z.string().min(1).max(100),
    githubToken: zod_1.z.string(),
    displayGitHubToken: zod_1.z.string(),
    lastFailingExecutionId: zod_1.z.string().nullish(),
});
exports.SafeGitHubDispatchActionConfigSchema = exports.GitHubDispatchActionConfigSchema.omit({
    githubToken: true,
});
exports.GitHubDispatchActionCreateSchema = zod_1.z.object({
    type: zod_1.z.literal("GITHUB_DISPATCH"),
    url: zod_1.z.url().optional(), // Optional for updates, validated in helper
    eventType: zod_1.z.string().min(1).max(100).optional(), // Optional for updates, validated in helper
    githubToken: zod_1.z.string().optional(), // Optional for updates, validated in helper
});
exports.ActionConfigSchema = zod_1.z.discriminatedUnion("type", [
    exports.WebhookActionConfigSchema,
    exports.SlackActionConfigSchema,
    exports.GitHubDispatchActionConfigSchema,
]);
exports.ActionCreateSchema = zod_1.z.discriminatedUnion("type", [
    exports.WebhookActionCreateSchema,
    exports.SlackActionConfigSchema,
    exports.GitHubDispatchActionCreateSchema,
]);
exports.SafeActionConfigSchema = zod_1.z.discriminatedUnion("type", [
    exports.SafeWebhookActionConfigSchema,
    exports.SlackActionConfigSchema,
    exports.SafeGitHubDispatchActionConfigSchema,
]);
// Type Guards for Runtime Validation
// Using existing Zod schemas to provide both compile-time and runtime type safety
/**
 * Type guard to check if a config is a valid webhook configuration with secrets
 */
function isWebhookActionConfig(config) {
    return exports.WebhookActionConfigSchema.safeParse(config).success;
}
/**
 * Type guard to check if a config is a valid Slack configuration
 */
function isSlackActionConfig(config) {
    return exports.SlackActionConfigSchema.safeParse(config).success;
}
/**
 * Type guard to check if an entire action has valid webhook configuration
 */
function isWebhookAction(action) {
    return action.type === "WEBHOOK" && isWebhookActionConfig(action.config);
}
/**
 * Type guard for safe webhook config (without secrets)
 */
function isSafeWebhookActionConfig(config) {
    return exports.SafeWebhookActionConfigSchema.safeParse(config).success;
}
/**
 * Converts webhook config with secrets to safe config by only including allowed fields
 */
function convertToSafeWebhookConfig(webhookConfig) {
    return {
        type: webhookConfig.type,
        url: webhookConfig.url,
        displayHeaders: webhookConfig.displayHeaders,
        apiVersion: webhookConfig.apiVersion,
        displaySecretKey: webhookConfig.displaySecretKey,
        lastFailingExecutionId: webhookConfig.lastFailingExecutionId,
    };
}
/**
 * Type guard to check if a config is a valid GitHub dispatch configuration with secrets
 */
function isGitHubDispatchActionConfig(config) {
    return exports.GitHubDispatchActionConfigSchema.safeParse(config).success;
}
/**
 * Type guard to check if an entire action has valid GitHub dispatch configuration
 */
function isGitHubDispatchAction(action) {
    return (action.type === "GITHUB_DISPATCH" &&
        isGitHubDispatchActionConfig(action.config));
}
/**
 * Type guard for safe GitHub dispatch config (without secrets)
 */
function isSafeGitHubDispatchActionConfig(config) {
    return exports.SafeGitHubDispatchActionConfigSchema.safeParse(config).success;
}
/**
 * Converts GitHub dispatch config with secrets to safe config by only including allowed fields
 */
function convertToSafeGitHubDispatchConfig(config) {
    return {
        type: config.type,
        url: config.url,
        eventType: config.eventType,
        displayGitHubToken: config.displayGitHubToken,
        lastFailingExecutionId: config.lastFailingExecutionId,
    };
}
